import { differenceInCalendarDays, eachDayOfInterval, endOfMonth, format, startOfMonth } from "date-fns";
import { enUS, pt } from "date-fns/locale";
import { sortAlphabetically } from "../../../utils/sortAlphabetically";
import { ABSENCE_TYPE, DEFAULT_ABSENCE_TYPES } from "../../../utils/constants/organization";

export const getDateLocale = (language) => (language?.startsWith("pt") ? pt : enUS);

export const toDateKey = (date) => format(date, "yyyy-MM-dd");

export const parseDateOnly = (value) => {
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const getMonthDays = (monthDate) =>
  eachDayOfInterval({ start: startOfMonth(monthDate), end: endOfMonth(monthDate) });

export const getBarPlacement = (absence, monthDate) => {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const start = parseDateOnly(absence.start_date);
  const end = parseDateOnly(absence.end_date);

  if (end < monthStart || start > monthEnd || end < start) return null;

  const visibleStart = start < monthStart ? monthStart : start;
  const visibleEnd = end > monthEnd ? monthEnd : end;

  return {
    startDay: visibleStart.getDate(),
    span: differenceInCalendarDays(visibleEnd, visibleStart) + 1,
  };
};

export const assignLanes = (placedAbsences) => {
  const laneEnds = [];
  const sorted = [...placedAbsences].sort(
    (a, b) => a.placement.startDay - b.placement.startDay || b.placement.span - a.placement.span,
  );

  const withLanes = sorted.map((item) => {
    const { startDay, span } = item.placement;
    let lane = laneEnds.findIndex((lastDay) => lastDay < startDay);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = startDay + span - 1;
    return { ...item, lane };
  });

  return { items: withLanes, laneCount: Math.max(laneEnds.length, 1) };
};

export const getFullName = (user) => [user.first_name, user.last_name].filter(Boolean).join(" ");

export const sortUsersByName = (users, locale) =>
  sortAlphabetically(users, (user) => [user.first_name, user.last_name], locale);

export const ABSENCE_TYPE_COLORS = {
  [ABSENCE_TYPE.VACATION]: "#97c598",
  [ABSENCE_TYPE.FAMILY_ASSISTANCE]: "#fcc076",
  [ABSENCE_TYPE.SICK_LEAVE]: "#e27472",
  [ABSENCE_TYPE.PERSONAL_LEAVE]: "#bd8fca",
  [ABSENCE_TYPE.EXAMS]: "#65aae6",
  [ABSENCE_TYPE.JUSTIFIED_ABSENCE]: "#87beb9",
  [ABSENCE_TYPE.SPECIFIC_HOLIDAY]: "#ffe883",
  [ABSENCE_TYPE.UNPAID_LEAVE]: "#8a6f66",
  [ABSENCE_TYPE.CONGRESS]: "#626fbe",
  [ABSENCE_TYPE.PARENTAL_LEAVE]: "#d691aa",
  [ABSENCE_TYPE.OTHER]: "#586e79",
};

const normalizeAbsenceType = (type) => String(type || "").trim().toLowerCase();

export const getAbsenceTypeColor = (type) =>
  ABSENCE_TYPE_COLORS[normalizeAbsenceType(type)] || ABSENCE_TYPE_COLORS[ABSENCE_TYPE.OTHER];

export const getPresentAbsenceTypes = (users) => {
  const present = new Set(
    users.flatMap((user) => (user.absences || []).map((absence) => normalizeAbsenceType(absence.absence_type))),
  );
  const known = DEFAULT_ABSENCE_TYPES.filter((type) => present.has(type));
  const unknown = [...present].filter((type) => !ABSENCE_TYPE_COLORS[type]);
  return unknown.length && !known.includes(ABSENCE_TYPE.OTHER) ? [...known, ABSENCE_TYPE.OTHER] : known;
};
